'use client';

import { type PropsWithChildren, createContext, useContext } from 'react';

import { useAccount } from 'reservations/account/providers/AccountProvider';
import { useConfigQuery } from 'reservations/account/queries/useConfigQuery';
import { type ConfigProviderData } from 'reservations/account/types';
import { REDIRECT_WITH_ACCOUNT_DETAILS } from 'reservations/base/constants/redirects';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';

const ConfigContext = createContext<ConfigProviderData | null>(null);

type Params = {
  slug: string;
};

export const ConfigProvider = ({
  slug,
  children
}: PropsWithChildren<Params>) => {
  const { data, isError, isPending } = useConfigQuery(slug);
  const { handleNavigation } = useCustomRouter();

  if (isPending) {
    return <></>;
  }

  // TODO: Figure out how to handle error state here in the future
  // istanbul ignore next
  if (isError) {
    console.error('Failed to load Nextable Config');
    return null;
  }

  if (!data.inGoodStanding) {
    handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
  }

  return (
    <ConfigContext.Provider value={{ ...data, slug }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useSlug = () => useConfig()?.slug;

export const useAccessToken = () => useConfig()?.access_token;

export const useReservationsConfig = () => useConfig()?.booking;

export const usePartySizeRange = () => ({
  min: useReservationsConfig()?.partySizeMin,
  max: useReservationsConfig()?.partySizeMax
});

export const useAcceptsReservations = () =>
  useReservationsConfig()?.acceptsParties;

export const useIsWaitlistOnly = () => !useReservationsConfig()?.featureEnabled;

export const useAcceptsWaitlist = () => useConfig()?.waitlist?.acceptsParties;

export const useFeePolicy = () => useConfig()?.feePolicy;

export const useHasFeePolicy = () => useFeePolicy()?.shouldCollect;

export const useLocation = () => useConfig()?.location;

export const useChecklessSettings = () => useConfig().checkless_payments!;

export const useDefaultTipAmount = () =>
  useChecklessSettings()?.default_tip ?? 15;

export const useIsChecklessPaymentsEnabled = () =>
  useChecklessSettings()?.is_checkless_payments_enabled ?? false;

export const useIsTipEditablePostClose = () =>
  useChecklessSettings()?.is_tip_editable_post_close ?? false;

const usePreAuthAmount = () => useChecklessSettings()?.pre_auth_amount;

export const usePreAuthFinePrint = () =>
  `Please note that your card will be pre-authorized for $${usePreAuthAmount()} when you're seated at the restaurant.`;

export const useIsTipEnabled = () =>
  useChecklessSettings()?.tip_available ?? true;

export const useWaitlistClosedText = () =>
  useConfig()?.waitlist.waitlist_closed_text;

export const useLocationId = () => useLocation().id;

export const useContactInfo = () => {
  const { phone_number: phoneNumber, email_address: emailAddress } =
    useLocation();
  const hasPhoneNumber = !!phoneNumber;
  const hasEmailAddress = !!emailAddress;
  const hasContactInfo = hasPhoneNumber || hasEmailAddress;

  return {
    hasPhoneNumber,
    hasEmailAddress,
    hasContactInfo,
    phoneNumber,
    emailAddress
  };
};

export const useRestaurantName = () => {
  const {
    connected_location_integrations: connectedLocationIntegrations,
    restaurantName
  } = useAccount();
  const { name: locationName } = useLocation();
  const isMultiLocation = connectedLocationIntegrations.length > 1;
  const name = isMultiLocation
    ? `${restaurantName} - ${locationName}`
    : restaurantName;

  return name;
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context == null) {
    throw new Error('useConfig must be used within ConfigProvider');
  }

  return context;
};
